import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";
import { Task } from "./taskboard-reducer";

const PRIORITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  high: { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
  medium: { bg: "#fffbeb", text: "#d97706", border: "#fde68a" },
  low: { bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" },
};

const PROJECT_COLORS = [
  "#f59e0b", "#10b981", "#3b82f6", "#ec4899",
  "#8b5cf6", "#0ea5e9", "#14b8a6", "#f43f5e", "#84cc16", "#6366f1",
];

function projectColor(id: number) {
  return PROJECT_COLORS[id % PROJECT_COLORS.length];
}

function initials(first: string, last: string) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();
}

interface Props {
  task: Task;
  onClick: () => void;
}

const TaskCard = ({ task, onClick }: Props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const pColor = projectColor(task.projectId);

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      sx={{
        backgroundColor: isDragging ? "#f8fafc" : "#fff",
        borderRadius: 1.5,
        p: 1.5,
        mb: 1,
        cursor: isDragging ? "grabbing" : "grab",
        boxShadow: isDragging
          ? "0 8px 24px rgba(0,0,0,0.15)"
          : "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
        border: task.archived ? "1px dashed #cbd5e1" : "1px solid #f1f5f9",
        opacity: isDragging ? 0.5 : task.archived ? 0.55 : 1,
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          borderColor: "#e2e8f0",
        },
        userSelect: "none",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
      }}
    >
      {/* Project label stripe */}
      {task.project && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
          <Box sx={{ width: 3, height: 12, borderRadius: 1, backgroundColor: pColor, flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              color: pColor,
              fontWeight: 700,
              fontSize: "0.65rem",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {task.project.name}
          </Typography>
        </Box>
      )}

      {/* Title */}
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, lineHeight: 1.45, color: "#1e293b", mb: task.priority || task.dueDate || task.assignee ? 1 : 0 }}
      >
        {task.title}
      </Typography>

      {/* Meta row — only renders fields that exist */}
      {(task.priority || task.dueDate || task.assignee) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          {task.priority && (
            <Chip
              label={task.priority}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                backgroundColor: PRIORITY_COLORS[task.priority].bg,
                color: PRIORITY_COLORS[task.priority].text,
                border: `1px solid ${PRIORITY_COLORS[task.priority].border}`,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}

          {task.dueDate && (() => {
            const overdue = !task.archived && dayjs(task.dueDate).isBefore(dayjs(), "day");
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, color: overdue ? "#ef4444" : "#94a3b8" }}>
                <CalendarTodayIcon sx={{ fontSize: 11 }} />
                <Typography variant="caption" sx={{ fontSize: "0.68rem", color: overdue ? "#ef4444" : "#94a3b8", fontWeight: overdue ? 700 : 400 }}>
                  {dayjs(task.dueDate).format("MMM D")}
                </Typography>
              </Box>
            );
          })()}

          {task.assignee && (
            <Tooltip title={`${task.assignee.firstName} ${task.assignee.lastName}`}>
              <Avatar
                sx={{
                  width: 22,
                  height: 22,
                  fontSize: "0.58rem",
                  fontWeight: 700,
                  ml: "auto",
                  backgroundColor: "#6366f1",
                }}
              >
                {initials(task.assignee.firstName, task.assignee.lastName)}
              </Avatar>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TaskCard;
